import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import Badge from "../../../components/badge";
import Alert from "../../../components/alert";
import Button from "../../../components/button";
import useScreenName from "../../../hooks/useScreenName";
import { dateToDDMMYYYYHHMMAP, splitKeyWithSpace } from "../../../utils";
import { RoutePaths } from "../../../models";
import dispatch, { ForbiddenError } from "../../../middleware";
import { getAuditLog } from "../../../actions/credit-decision.action";
import { CreditDecisionAuditLog } from "../../../models/creditDecision";
import { buildUnderwritingUiExecutionUrl } from "../../underwriting-ui/underwritingUi.constants";

export default function AuditLogDetailPage() {
  useScreenName("Audit Log Detail");
  const { id } = useParams() as { id: string };
  const history = useHistory();
  const storeDispatch = useDispatch();

  const [log, setLog] = useState<CreditDecisionAuditLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [notAuthorized, setNotAuthorized] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(storeDispatch, getAuditLog(id))
      .then((data: CreditDecisionAuditLog) => setLog(data))
      .catch((error) => {
        if (error instanceof ForbiddenError) {
          setNotAuthorized(true);
        } else {
          alert("Failed to fetch audit log detail");
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function handleBack() {
    history.push(RoutePaths.CREDIT_DECISION_AUDIT_LOGS);
  }

  const executionId = log?.responseSnapshot?.salaryTopup?.executionId;

  if (loading) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Audit Log Detail</h2>
          <Button variant="blue-outline" onClick={handleBack}>
            ← Back to Logs
          </Button>
        </div>
        <div className="dashboard-container">
          <p className="text-center text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  if (notAuthorized) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Audit Log Detail</h2>
          <Button variant="blue-outline" onClick={handleBack}>
            ← Back to Logs
          </Button>
        </div>
        <div className="dashboard-container">
          <Alert type="danger" message="Not authorized to view this audit log." />
        </div>
      </div>
    );
  }

  if (!log) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Audit Log Detail</h2>
          <Button variant="blue-outline" onClick={handleBack}>
            ← Back to Logs
          </Button>
        </div>
        <div className="dashboard-container">
          <p className="text-center text-danger">Audit log not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Audit Log Detail</h2>
        <Button variant="blue-outline" onClick={handleBack}>
          ← Back to Logs
        </Button>
      </div>

      <div className="dashboard-container mb-4">
        <h5 className="mb-3">Metadata</h5>
        <table className="table table-sm table-bordered">
          <tbody>
            <tr>
              <td className="fw-bold" style={{ width: "30%" }}>
                ID
              </td>
              <td style={{ wordBreak: "break-all" }}>{log._id}</td>
            </tr>
            <tr>
              <td className="fw-bold">Product Type</td>
              <td>{splitKeyWithSpace(log.productType)}</td>
            </tr>
            <tr>
              <td className="fw-bold">Approved</td>
              <td>
                <Badge type={log.approved ? "success" : "reject"}>
                  {log.approved ? "Yes" : "No"}
                </Badge>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">User ID</td>
              <td>{log.userId || "-"}</td>
            </tr>
            <tr>
              <td className="fw-bold">Worker ID</td>
              <td>{log.workerId || "-"}</td>
            </tr>
            <tr>
              <td className="fw-bold">Employer ID</td>
              <td>{log.employerId || "-"}</td>
            </tr>
            <tr>
              <td className="fw-bold">Client User ID</td>
              <td>{log.clientReferences?.clientUserId || "-"}</td>
            </tr>
            <tr>
              <td className="fw-bold">Client Worker ID</td>
              <td>{log.clientReferences?.clientWorkerId || "-"}</td>
            </tr>
            <tr>
              <td className="fw-bold">Client Employer ID</td>
              <td>{log.clientReferences?.clientEmployerId || "-"}</td>
            </tr>
            {executionId && (
              <tr>
                <td className="fw-bold">Execution ID</td>
                <td>
                  <Link to={buildUnderwritingUiExecutionUrl(executionId)}>
                    {executionId}
                  </Link>
                </td>
              </tr>
            )}
            <tr>
              <td className="fw-bold">Evaluated At</td>
              <td>{log.evaluatedAt ? dateToDDMMYYYYHHMMAP(log.evaluatedAt) : "-"}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="dashboard-container">
        <h5 className="mb-3">Outcome</h5>
        <pre
          className="bg-light p-3 rounded"
          style={{ maxHeight: "500px", overflow: "auto", fontSize: "12px" }}
        >
          {JSON.stringify(log.responseSnapshot, null, 2)}
        </pre>
      </div>
    </div>
  );
}
